export type Rank = 0 | 1 | 2 | 3 | 4;

export const RANK_NAMES: Record<Rank, string> = {
  0: 'untrained',
  1: 'trained',
  2: 'expert',
  3: 'master',
  4: 'legendary',
};

/** A rank by its name: what a hidden hazard's minimum proficiency asks of whoever would find it. */
export const RANK_BY_NAME: Record<'untrained' | 'trained' | 'expert' | 'master' | 'legendary', Rank> = {
  untrained: 0, trained: 1, expert: 2, master: 3, legendary: 4,
};

/** PF2e: untrained adds nothing; otherwise level + 2/4/6/8. */
export function proficiencyBonus(rank: Rank, level: number): number {
  return rank === 0 ? 0 : level + 2 * rank;
}

export type SkillId =
  | 'acrobatics' | 'arcana' | 'athletics' | 'crafting' | 'deception' | 'diplomacy' | 'intimidation' | 'medicine'
  | 'nature' | 'occultism' | 'performance' | 'religion' | 'society' | 'stealth' | 'survival' | 'thievery';

export const SKILLS: Record<SkillId, { name: string; attribute: 'str' | 'dex' | 'int' | 'wis' | 'cha' }> = {
  acrobatics: { name: 'Acrobatics', attribute: 'dex' },
  arcana: { name: 'Arcana', attribute: 'int' },
  athletics: { name: 'Athletics', attribute: 'str' },
  crafting: { name: 'Crafting', attribute: 'int' },
  deception: { name: 'Deception', attribute: 'cha' },
  diplomacy: { name: 'Diplomacy', attribute: 'cha' },
  intimidation: { name: 'Intimidation', attribute: 'cha' },
  medicine: { name: 'Medicine', attribute: 'wis' },
  nature: { name: 'Nature', attribute: 'wis' },
  occultism: { name: 'Occultism', attribute: 'int' },
  performance: { name: 'Performance', attribute: 'cha' },
  religion: { name: 'Religion', attribute: 'wis' },
  society: { name: 'Society', attribute: 'int' },
  stealth: { name: 'Stealth', attribute: 'dex' },
  survival: { name: 'Survival', attribute: 'wis' },
  thievery: { name: 'Thievery', attribute: 'dex' },
};

export const SKILL_IDS = Object.keys(SKILLS) as SkillId[];

/**
 * Keys a creature can hold a proficiency rank in. Armor/weapon keys are by category;
 * `weapon-group:<group>` covers the simple and martial weapons and unarmed attacks of one weapon
 * group (Fighter Weapon Mastery, Weapon Legend), and `weapon-group:<group>:advanced` its advanced
 * weapons (a gunslinger's advanced firearms and crossbows).
 */
export type ProficiencyKey =
  | 'perception' | 'fort' | 'ref' | 'will' | 'class-dc' | 'spellcasting'
  | 'armor:unarmored' | 'armor:light' | 'armor:medium' | 'armor:heavy'
  | 'weapon:unarmed' | 'weapon:simple' | 'weapon:martial' | 'weapon:advanced'
  // Also matches 'weapon-group:firearm:advanced'.
  | `weapon-group:${string}`
  | `skill:${SkillId}`
  // Warfare Lore: a commander's lore. Not a skill (no skill list holds it): only its class raises it.
  | 'lore:warfare';
